import * as THREE from "three";

import type { LoomAuthoringDocument, LoomSceneNode } from "./loomAuthoringModel";
import type { LoomNativeHybridLayers } from "./LoomNativeHybridViewer";
import { addTessellation, createIndexedGeometry } from "./loomNativeViewerHelpers";
import { addLoomSelectionOutlines } from "./loomSelectionOutline";
import {
  addLoomHybridSurface,
  type LoomSurfaceAppearance,
} from "./loomViewportSurfaceMaterial";

export type LoomSurfaceStyle = "filled" | "filled-wire" | "wireframe";
export type LoomAssemblyView = "assembled" | "exploded" | "highlight-only";

export interface LoomViewportPresentationState {
  assemblyView: LoomAssemblyView;
  explodeAmount: number;
  surfaceStyle: LoomSurfaceStyle;
}

export const LOOM_DEFAULT_VIEWPORT_PRESENTATION: LoomViewportPresentationState = {
  assemblyView: "assembled",
  explodeAmount: 0.4,
  surfaceStyle: "filled",
};

export interface LoomOwnedDisplaySurface {
  faceEntityIds: readonly string[];
  faces: Uint32Array;
  geometrySource: "retained-display-faces";
  positions: Float32Array;
}

export interface LoomSurfacePresentationResult {
  activeFillVisible: boolean;
  bounds: THREE.Box3;
  entityCount: number;
  explodedEntityCount: number;
  filledMeshCount: number;
  group: THREE.Group;
  highlightFaceCount: number;
  highlightOwnerCount: number;
  offsetSignature: string;
  outlineCount: number;
  ownerIds: readonly string[];
  presentedGeometrySignature: string;
  unavailableReason: string | null;
  visibleEntityCount: number;
  wireMeshCount: number;
}

export function loomSurfaceStyleFromLayers(
  surface: boolean,
  tessellation: boolean,
): LoomSurfaceStyle {
  if (surface && tessellation) return "filled-wire";
  return surface ? "filled" : "wireframe";
}

export function loomLayersForSurfaceStyle(style: LoomSurfaceStyle): {
  surface: boolean;
  tessellation: boolean;
} {
  return {
    surface: style !== "wireframe",
    tessellation: style !== "filled",
  };
}

export function toggleLoomPresentationLayer(
  current: LoomNativeHybridLayers,
  name: keyof LoomNativeHybridLayers,
): LoomNativeHybridLayers {
  const enabled = !current[name];
  if (name === "parameterization" && enabled) {
    return { ...current, parameterization: true, orderedPanels: false, surface: true, tessellation: false };
  }
  if (name === "tessellation" && enabled) {
    return { ...current, parameterization: false, tessellation: true, orderedPanels: false };
  }
  if (name === "surface" && enabled) return { ...current, surface: true, orderedPanels: false };
  return { ...current, [name]: enabled };
}

export function loomUntransformedPositionSignature(positions: Float32Array): string {
  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  let weighted = 0;
  const count = positions.length / 3;
  for (let index = 0; index < count; index += 1) {
    const x = positions[index * 3]!;
    const y = positions[index * 3 + 1]!;
    const z = positions[index * 3 + 2]!;
    sumX += x;
    sumY += y;
    sumZ += z;
    weighted += (x * 3 + y * 5 + z * 7) * (((index + 1) % 97) + 1);
  }
  return [count, sumX, sumY, sumZ, weighted]
    .map((value, index) => index === 0 ? String(value) : value.toFixed(5)).join(":");
}

function nodeInputs(node: LoomSceneNode): readonly string[] {
  if (node.type === "boolean") return node.inputNodeIds;
  if (node.type === "transform") return [node.inputNodeId];
  return [];
}

export function resolveLoomSelectedEntityIds(
  document: LoomAuthoringDocument,
  selectedNodeId: string,
  availableEntityIds: ReadonlySet<string>,
): readonly string[] {
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const resolved = new Set<string>();
  const visit = (nodeId: string, stack: ReadonlySet<string>): void => {
    if (stack.has(nodeId)) return;
    const node = nodes.get(nodeId);
    if (!node) return;
    if (availableEntityIds.has(node.id)) {
      resolved.add(node.id);
      return;
    }
    const next = new Set(stack);
    next.add(nodeId);
    nodeInputs(node).forEach((input) => visit(input, next));
  };
  visit(selectedNodeId, new Set());
  const reachesSelectionWithoutMirror = (nodeId: string, stack: ReadonlySet<string>): boolean => {
    if (nodeId === selectedNodeId) return true;
    if (stack.has(nodeId)) return false;
    const node = nodes.get(nodeId);
    if (!node || (node.type === "transform"
      && (node.operation.transform === "mirror" || node.operation.transform === "mirror-y"))) return false;
    const next = new Set(stack);
    next.add(nodeId);
    return nodeInputs(node).some((input) => reachesSelectionWithoutMirror(input, next));
  };
  availableEntityIds.forEach((entityId) => {
    if (reachesSelectionWithoutMirror(entityId, new Set())) resolved.add(entityId);
  });
  return [...resolved].sort();
}

function stableDirection(id: string): THREE.Vector3 {
  let hash = 2166136261;
  for (let index = 0; index < id.length; index += 1) {
    hash ^= id.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  const component = (shift: number) => (((hash >>> shift) & 255) / 127.5) - 1;
  const direction = new THREE.Vector3(component(0), component(8), component(16));
  if (direction.lengthSq() < 1e-6) direction.set(0.67, -0.51, 0.54);
  return direction.normalize();
}

export function deriveLoomExplodeOffsets(
  entityBounds: ReadonlyMap<string, THREE.Box3>,
  amount: number,
): ReadonlyMap<string, THREE.Vector3> {
  const ids = [...entityBounds.keys()].sort();
  const assembly = new THREE.Box3();
  ids.forEach((id) => assembly.union(entityBounds.get(id)!));
  const center = assembly.getCenter(new THREE.Vector3());
  const diagonal = Math.max(assembly.getSize(new THREE.Vector3()).length(), 1e-6);
  const distance = diagonal * 0.16 * THREE.MathUtils.clamp(amount, 0, 1);
  return new Map(ids.map((id) => {
    const direction = entityBounds.get(id)!.getCenter(new THREE.Vector3()).sub(center);
    if (direction.lengthSq() < diagonal * diagonal * 1e-10) direction.copy(stableDirection(id));
    else direction.normalize();
    return [id, direction.multiplyScalar(distance)] as const;
  }));
}

function groupedFaces(surface: LoomOwnedDisplaySurface): Map<string, number[]> {
  if (surface.faceEntityIds.length !== surface.faces.length / 3) {
    throw new Error("display face ownership does not match the retained surface");
  }
  const grouped = new Map<string, number[]>();
  surface.faceEntityIds.forEach((id, face) => {
    if (!id) throw new Error("display face ownership contains an empty entity id");
    const values = grouped.get(id) ?? [];
    values.push(surface.faces[face * 3]!, surface.faces[face * 3 + 1]!, surface.faces[face * 3 + 2]!);
    grouped.set(id, values);
  });
  return grouped;
}

function geometryBounds(positions: Float32Array, faces: readonly number[]): THREE.Box3 {
  const bounds = new THREE.Box3();
  const point = new THREE.Vector3();
  for (const vertex of new Set(faces)) {
    point.fromArray(positions, vertex * 3);
    bounds.expandByPoint(point);
  }
  return bounds;
}

function compactOwnedGeometry(
  positions: Float32Array,
  faces: Uint32Array,
  sourceNormals: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
): { faces: Uint32Array; normals: Float32Array; positions: Float32Array } {
  const remap = new Map<number, number>();
  const compactFaces: number[] = [];
  const compactNormals: number[] = [];
  const compactPositions: number[] = [];
  faces.forEach((sourceVertex) => {
    let target = remap.get(sourceVertex);
    if (target === undefined) {
      target = remap.size;
      remap.set(sourceVertex, target);
      compactPositions.push(
        positions[sourceVertex * 3]!, positions[sourceVertex * 3 + 1]!, positions[sourceVertex * 3 + 2]!,
      );
      compactNormals.push(
        sourceNormals.getX(sourceVertex), sourceNormals.getY(sourceVertex), sourceNormals.getZ(sourceVertex),
      );
    }
    compactFaces.push(target);
  });
  return {
    faces: new Uint32Array(compactFaces),
    normals: new Float32Array(compactNormals),
    positions: new Float32Array(compactPositions),
  };
}

function amberOverlay(geometry: THREE.BufferGeometry, color: string): THREE.Mesh {
  const material = new THREE.MeshPhysicalMaterial({
    clearcoat: 0.18,
    color,
    depthTest: true,
    depthWrite: false,
    emissive: color,
    emissiveIntensity: 0.38,
    opacity: 0.5,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    roughness: 0.42,
    side: THREE.DoubleSide,
    transparent: true,
  });
  material.userData = {
    loomActiveSurfaceOverlay: true,
    loomGeometrySource: "retained-display-faces",
  };
  const overlay = new THREE.Mesh(geometry, material);
  overlay.name = "Active retained-face amber overlay";
  overlay.raycast = () => undefined;
  overlay.renderOrder = 24;
  overlay.userData.loomVisualRole = "active-part-fill";
  return overlay;
}

function offsetSignature(offsets: ReadonlyMap<string, THREE.Vector3>): string {
  if (offsets.size === 0) return "none";
  const number = (value: number) => Math.abs(value) < 5e-7 ? "0" : value.toFixed(6);
  return [...offsets.entries()].sort(([left], [right]) => left.localeCompare(right))
    .map(([id, value]) => `${id}:${number(value.x)},${number(value.y)},${number(value.z)}`)
    .join("|");
}

function meshSignature(meshes: readonly THREE.Mesh[]): string {
  const point = new THREE.Vector3();
  let count = 0;
  let sumX = 0;
  let sumY = 0;
  let sumZ = 0;
  let weighted = 0;
  meshes.forEach((mesh) => {
    const positions = mesh.geometry.getAttribute("position");
    for (let index = 0; index < positions.count; index += 1) {
      point.fromBufferAttribute(positions, index).applyMatrix4(mesh.matrixWorld);
      count += 1;
      sumX += point.x;
      sumY += point.y;
      sumZ += point.z;
      weighted += (point.x * 3 + point.y * 5 + point.z * 7) * ((count % 97) + 1);
    }
  });
  return [count, sumX, sumY, sumZ, weighted]
    .map((value, index) => index === 0 ? String(value) : value.toFixed(5)).join(":");
}

export function buildLoomOwnedSurfacePresentation(
  surface: LoomOwnedDisplaySurface,
  document: LoomAuthoringDocument,
  selectedNodeId: string,
  state: LoomViewportPresentationState,
  appearance: LoomSurfaceAppearance,
  surfaceColor: string,
  activeColor: string,
): LoomSurfacePresentationResult {
  const grouped = groupedFaces(surface);
  const ownerIds = [...grouped.keys()].sort();
  const available = new Set(ownerIds);
  const highlighted = new Set(resolveLoomSelectedEntityIds(document, selectedNodeId, available));
  const highlightOnly = state.assemblyView === "highlight-only";
  const unavailableReason = highlightOnly && highlighted.size === 0
    ? "Highlight only is unavailable because the selected node owns no visible retained faces."
    : null;
  const entityBounds = new Map(ownerIds.map((id) => [id, geometryBounds(surface.positions, grouped.get(id)!)]));
  const offsets = state.assemblyView === "exploded"
    ? deriveLoomExplodeOffsets(entityBounds, state.explodeAmount)
    : new Map<string, THREE.Vector3>();
  const group = new THREE.Group();
  group.name = "Loom retained-face presentation";
  let activeFillVisible = false;
  let filledMeshCount = 0;
  let highlightFaceCount = 0;
  let visibleEntityCount = 0;
  let wireMeshCount = 0;
  const visibleBounds = new THREE.Box3();
  const signatureMeshes: THREE.Mesh[] = [];
  const combinedGeometry = createIndexedGeometry(surface.positions, surface.faces);
  const combinedNormals = combinedGeometry.getAttribute("normal");

  ownerIds.forEach((id) => {
    const faces = new Uint32Array(grouped.get(id)!);
    const compact = compactOwnedGeometry(surface.positions, faces, combinedNormals);
    const selected = highlighted.has(id);
    const visible = !highlightOnly || selected;
    const entity = new THREE.Group();
    entity.name = `Presented entity: ${id}`;
    entity.userData.loomPresentationEntityId = id;
    entity.visible = visible;
    entity.position.copy(offsets.get(id) ?? new THREE.Vector3());
    const mesh = addLoomHybridSurface(entity, compact.positions, compact.faces, surfaceColor, `Retained faces: ${id}`, appearance);
    mesh.geometry.setAttribute("normal", new THREE.BufferAttribute(compact.normals, 3));
    if (visible) signatureMeshes.push(mesh);
    Object.assign(mesh.userData, {
      loomActiveSelection: selected,
      loomEditableNodeId: id,
      loomEntityFaceCount: faces.length / 3,
      loomGeometrySource: surface.geometrySource,
      loomNodeId: id,
    });
    if (state.surfaceStyle === "wireframe") {
      mesh.material.depthWrite = false;
      mesh.material.opacity = 0;
      mesh.material.transparent = true;
    } else {
      filledMeshCount += visible ? 1 : 0;
    }
    if (state.surfaceStyle !== "filled") {
      const wire = addTessellation(entity, mesh.geometry, `Retained-face wireframe: ${id}`);
      wire.userData.loomPresentationEntityId = id;
      if (selected) {
        const material = wire.material as THREE.LineBasicMaterial;
        material.color.set(activeColor);
        material.opacity = 0.94;
      }
      wireMeshCount += visible ? 1 : 0;
    }
    if (selected) {
      highlightFaceCount += faces.length / 3;
      if (state.surfaceStyle !== "wireframe") {
        entity.add(amberOverlay(mesh.geometry, activeColor));
        activeFillVisible = visible;
      }
    }
    group.add(entity);
    if (visible) {
      visibleEntityCount += 1;
      visibleBounds.union(entityBounds.get(id)!.clone().translate(entity.position));
    }
  });
  const outlineCount = addLoomSelectionOutlines(group, activeColor);
  combinedGeometry.dispose();
  group.updateMatrixWorld(true);
  return {
    activeFillVisible,
    bounds: visibleBounds,
    entityCount: ownerIds.length,
    explodedEntityCount: state.assemblyView === "exploded" ? visibleEntityCount : 0,
    filledMeshCount,
    group,
    highlightFaceCount,
    highlightOwnerCount: highlighted.size,
    offsetSignature: offsetSignature(offsets),
    outlineCount,
    ownerIds,
    presentedGeometrySignature: meshSignature(signatureMeshes),
    unavailableReason,
    visibleEntityCount,
    wireMeshCount,
  };
}

function worldOffsetInParent(mesh: THREE.Mesh, offset: THREE.Vector3): THREE.Vector3 {
  const inverse = mesh.parent?.matrixWorld.clone().invert() ?? new THREE.Matrix4();
  const origin = new THREE.Vector3().applyMatrix4(inverse);
  return offset.clone().applyMatrix4(inverse).sub(origin);
}

export function applyLoomAuthoredOperandPresentation(
  scene: THREE.Group,
  document: LoomAuthoringDocument,
  selectedNodeId: string,
  state: LoomViewportPresentationState,
  activeColor: string,
): LoomSurfacePresentationResult {
  scene.updateMatrixWorld(true);
  const grouped = new Map<string, THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>[]>();
  scene.traverse((object) => {
    const mesh = object as THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>;
    const id = mesh.userData.loomEditableNodeId;
    if (!mesh.isMesh || typeof id !== "string" || mesh.userData.loomVisualRole) return;
    grouped.set(id, [...(grouped.get(id) ?? []), mesh]);
  });
  const ownerIds = [...grouped.keys()].sort();
  const highlighted = new Set(resolveLoomSelectedEntityIds(document, selectedNodeId, new Set(ownerIds)));
  const unavailableReason = state.assemblyView === "highlight-only" && highlighted.size === 0
    ? "Highlight only is unavailable because the selected node owns no visible authored operands."
    : null;
  const entityBounds = new Map(ownerIds.map((id) => {
    const bounds = new THREE.Box3();
    grouped.get(id)!.forEach((mesh) => bounds.union(new THREE.Box3().setFromObject(mesh)));
    return [id, bounds] as const;
  }));
  const offsets = state.assemblyView === "exploded"
    ? deriveLoomExplodeOffsets(entityBounds, state.explodeAmount)
    : new Map<string, THREE.Vector3>();
  const visibleBounds = new THREE.Box3();
  const signatureMeshes: THREE.Mesh[] = [];
  let activeFillVisible = false;
  let filledMeshCount = 0;
  let highlightFaceCount = 0;
  let visibleEntityCount = 0;
  let wireMeshCount = 0;
  ownerIds.forEach((id) => {
    const selected = highlighted.has(id);
    const visible = state.assemblyView !== "highlight-only" || selected;
    const offset = offsets.get(id) ?? new THREE.Vector3();
    grouped.get(id)!.forEach((mesh) => {
      mesh.visible = visible;
      mesh.position.add(worldOffsetInParent(mesh, offset));
      mesh.material.wireframe = state.surfaceStyle === "wireframe";
      mesh.material.emissive.set(selected ? activeColor : "#000000");
      mesh.material.emissiveIntensity = selected ? 0.34 : 0;
      if (visible) signatureMeshes.push(mesh);
      const faceCount = (mesh.geometry.index?.count ?? mesh.geometry.getAttribute("position").count) / 3;
      if (selected) highlightFaceCount += faceCount;
      if (selected && state.surfaceStyle !== "wireframe") {
        mesh.add(amberOverlay(mesh.geometry, activeColor));
        activeFillVisible = visible;
      }
      if (state.surfaceStyle === "filled-wire") {
        const wires = new THREE.Group();
        wires.raycast = () => undefined;
        addTessellation(wires, mesh.geometry, `Authored operand wireframe: ${id}`);
        mesh.add(wires);
      }
    });
    if (visible) {
      visibleEntityCount += 1;
      visibleBounds.union(entityBounds.get(id)!.clone().translate(offset));
      if (state.surfaceStyle === "wireframe") wireMeshCount += 1;
      else {
        filledMeshCount += 1;
        if (state.surfaceStyle === "filled-wire") wireMeshCount += 1;
      }
    }
  });
  scene.updateMatrixWorld(true);
  return {
    activeFillVisible,
    bounds: visibleBounds,
    entityCount: ownerIds.length,
    explodedEntityCount: state.assemblyView === "exploded" ? visibleEntityCount : 0,
    filledMeshCount,
    group: scene,
    highlightFaceCount,
    highlightOwnerCount: highlighted.size,
    offsetSignature: offsetSignature(offsets),
    outlineCount: Number(scene.userData.loomActiveOutlineCount ?? 0),
    ownerIds,
    presentedGeometrySignature: meshSignature(signatureMeshes),
    unavailableReason,
    visibleEntityCount,
    wireMeshCount,
  };
}
